import React, { useEffect, useState } from "react";
import { api, ApiClientError } from "../api";
import type { AppointmentSlot, ContactDetail, Id, Opportunity, Professional, StaffUser, TimelineEvent } from "../types";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "../router";
import { formatCurrency, formatDateTime, eventLabel } from "../lib/format";
import QuoteBuilder from "./QuoteBuilder";
import PostventaTab from "./PostventaTab";
import AgendaBooking from "./AgendaBooking";

// Fase 1 clínica: roles que pueden entrar a la Historia Clínica 360°
// (coincide con CLINICAL_READ_ROLES del backend — reception queda afuera).
const CLINICAL_ACCESS_ROLES = ["admin", "director", "professional"];

/** `GET /opportunities/:id` returns `owner_name`; opportunities embedded in
 * `GET /contacts/:id` only carry `owner_user_id`. Fall back to the /users
 * list so the owner's name always renders regardless of which endpoint the
 * opportunity object came from. */
function resolveOwnerName(opp: Opportunity | undefined, users: StaffUser[]): string {
  if (!opp) return "Sin asignar";
  if (opp.owner_name) return opp.owner_name;
  if (opp.owner_user_id === null || opp.owner_user_id === undefined) return "Sin asignar";
  const match = users.find((u) => String(u.id) === String(opp.owner_user_id));
  return match?.full_name || "Sin asignar";
}

type TabKey = "resumen" | "timeline" | "conversacion" | "oportunidades" | "comercial" | "agenda" | "postventa" | "datos";

const TABS: { key: TabKey; label: string }[] = [
  { key: "resumen", label: "Resumen" },
  { key: "timeline", label: "Timeline" },
  { key: "conversacion", label: "Conversación" },
  { key: "oportunidades", label: "Oportunidades" },
  { key: "comercial", label: "Comercial" },
  { key: "agenda", label: "Agenda" },
  { key: "postventa", label: "Postventa" },
  { key: "datos", label: "Datos" },
];

export default function ContactDrawer({ contactId, onClose }: { contactId: import("../types").Id; onClose: () => void }) {
  const { show } = useToast();
  const { isAdminLike, user } = useAuth();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<ContactDetail | null>(null);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [users, setUsers] = useState<StaffUser[]>([]);
  const [tab, setTab] = useState<TabKey>("resumen");
  const [noteBody, setNoteBody] = useState("");
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDue, setTaskDue] = useState("");
  const [taskAssignee, setTaskAssignee] = useState("");
  const [assignOwner, setAssignOwner] = useState("");

  const contact = detail?.contact;
  const opportunities = detail?.opportunities || [];
  const tags = detail?.tags || [];
  const primaryOpp: Opportunity | undefined = opportunities[0];

  async function reload() {
    try {
      const [c, t] = await Promise.all([api.getContact(contactId), api.getTimeline(contactId)]);
      setDetail(c);
      setTimeline(t);
    } catch (e) {
      show("No se pudo cargar la ficha 360°.", "error");
    }
  }

  useEffect(() => {
    reload();
    api.users().then(setUsers).catch(() => {});
    setTab("resumen");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  async function addNote() {
    if (!primaryOpp || !noteBody.trim()) return;
    try {
      await api.addNote({ opportunity_id: primaryOpp.id, body: noteBody.trim() });
      setNoteBody("");
      show("Nota agregada.", "success");
      reload();
    } catch (e) {
      show("No se pudo agregar la nota.", "error");
    }
  }

  async function addTask() {
    if (!primaryOpp || !taskTitle.trim() || !taskDue || !taskAssignee) {
      show("Completá título, vencimiento y responsable.", "error");
      return;
    }
    try {
      await api.addTask({
        opportunity_id: primaryOpp.id,
        title: taskTitle.trim(),
        due_at: new Date(taskDue).toISOString(),
        assigned_user_id: taskAssignee,
      });
      setTaskTitle("");
      setTaskDue("");
      setTaskAssignee("");
      show("Tarea creada.", "success");
      reload();
    } catch (e) {
      show("No se pudo crear la tarea.", "error");
    }
  }

  async function reassignOwner() {
    if (!primaryOpp || !assignOwner) return;
    try {
      await api.assign(primaryOpp.id, { owner_user_id: assignOwner });
      show("Responsable actualizado.", "success");
      reload();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo reasignar.", "error");
    }
  }

  if (!detail || !contact) {
    return (
      <aside className="drawer open">
        <button className="close" onClick={onClose}>
          Cerrar
        </button>
        <p>Cargando...</p>
      </aside>
    );
  }

  return (
    <aside className="drawer open">
      <button className="close" onClick={onClose}>
        Cerrar
      </button>
      <h2>{contact.full_name}</h2>
      <div className="meta">
        {contact.phone} {contact.email ? `· ${contact.email}` : ""}
        {primaryOpp?.stage_name ? ` · Etapa: ${primaryOpp.stage_name}` : ""}
      </div>
      {user && CLINICAL_ACCESS_ROLES.includes(user.role_code) && (
        <button
          className="btn-primary"
          style={{ marginTop: 8 }}
          onClick={() => {
            onClose();
            navigate(`/pacientes/${contact.id}/historia`);
          }}
        >
          Historia Clínica 360° →
        </button>
      )}
      {tags.length > 0 && (
        <div className="row" style={{ marginTop: 8 }}>
          {tags.map((t) => (
            <span key={t} className="badge">
              {t}
            </span>
          ))}
        </div>
      )}

      <div className="drawer-tabs">
        {TABS.map((t) => (
          <button key={t.key} className={`tab ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="drawer-body">
        {tab === "resumen" && (
          <div>
            <h3>Resumen IA</h3>
            <p className="meta">Próximamente — resumen generado automáticamente a partir de la conversación con el paciente.</p>
            <h3>Próxima acción</h3>
            <p>{primaryOpp?.next_action_text || "Sin próxima acción registrada."}</p>
            <p className="meta">{primaryOpp?.next_action_at ? formatDateTime(primaryOpp.next_action_at) : ""}</p>
            <h3>Tareas abiertas</h3>
            <OpenTasksList opportunityId={primaryOpp?.id} />
          </div>
        )}

        {tab === "timeline" && (
          <div className="timeline">
            {timeline.length === 0 && <p className="meta">Sin eventos todavía.</p>}
            {timeline.map((ev) => (
              <div className="event" key={ev.id}>
                <b>{eventLabel(ev.event_type)}</b>
                <small>{formatDateTime(ev.created_at)}</small>
                {ev.payload && Object.keys(ev.payload).length > 0 && (
                  <div className="meta">
                    {Object.entries(ev.payload)
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(" · ")}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "conversacion" && (
          <div>
            <p className="meta">Próximamente — integración WhatsApp.</p>
          </div>
        )}

        {tab === "oportunidades" && (
          <div>
            {opportunities.map((o) => (
              <div key={o.id} className="opp-item">
                <div className="name">{o.service_name || o.service_code}</div>
                <div className="meta">
                  {o.pipeline_code} · {o.stage_name || o.stage_code} · {formatCurrency(o.expected_value)}
                </div>
                <div className="meta">Responsable: {resolveOwnerName(o, users)}</div>
              </div>
            ))}
            {opportunities.length === 0 && <p className="meta">Sin oportunidades registradas.</p>}
          </div>
        )}

        {tab === "comercial" && (
          <div>
            <h3>Servicio de interés</h3>
            <p>{primaryOpp?.service_name || "-"}</p>
            <h3>Valor estimado</h3>
            <p>{formatCurrency(primaryOpp?.expected_value)}</p>
            {primaryOpp ? (
              <QuoteBuilder opportunityId={primaryOpp.id} />
            ) : (
              <p className="meta">Necesitás una oportunidad para armar un presupuesto.</p>
            )}
          </div>
        )}

        {tab === "agenda" && (
          <div>
            <h3>Reservar turno</h3>
            {primaryOpp?.service_id ? (
              <AgendaBooking
                  opportunityId={primaryOpp.id}
                  serviceId={primaryOpp.service_id}
                  onBooked={reload}
                />
            ) : (
              <p className="meta">
                  Necesitás una oportunidad con tratamiento asignado
                  para reservar un turno.
                </p>
            )}
          </div>
        )}

        {tab === "postventa" && (
          <div>
            <PostventaTab contactId={contact.id} />
          </div>
        )}

        {tab === "datos" && (
          <div>
            <h3>Datos del contacto</h3>
            <div className="data-grid">
              <div>
                <label>Nombre</label>
                <div>{contact.full_name}</div>
              </div>
              <div>
                <label>Teléfono</label>
                <div>{contact.phone}</div>
              </div>
              <div>
                <label>Email</label>
                <div>{contact.email || "-"}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <hr />

      <div className="right-rail">
        <h3>Responsable</h3>
        <p>{resolveOwnerName(primaryOpp, users)}</p>
        {isAdminLike && primaryOpp && (
          <div className="row">
            <select value={assignOwner} onChange={(e: any) => setAssignOwner(e.target.value)}>
              <option value="">Reasignar a...</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name}
                </option>
              ))}
            </select>
            <button className="btn-ghost" disabled={!assignOwner} onClick={reassignOwner}>
              Asignar
            </button>
          </div>
        )}

        <h3>Colaboradores</h3>
        <p className="meta">{primaryOpp?.collaborators?.map((c) => c.full_name).join(", ") || "Ninguno"}</p>

        <h3>SLA</h3>
        <p className={primaryOpp?.sla?.status === "breach" ? "overdue" : primaryOpp?.sla?.status === "warning" ? "warning" : ""}>
          {primaryOpp?.sla?.status || "-"}
        </p>

        <h3>Agregar nota</h3>
        <textarea value={noteBody} onChange={(e: any) => setNoteBody(e.target.value)} rows={2} placeholder="Escribí una nota..." />
        <button className="btn-ghost" onClick={addNote} disabled={!primaryOpp}>
          Guardar nota
        </button>

        <h3>Agregar tarea</h3>
        <input placeholder="Título" value={taskTitle} onChange={(e: any) => setTaskTitle(e.target.value)} />
        <input type="datetime-local" value={taskDue} onChange={(e: any) => setTaskDue(e.target.value)} />
        <select value={taskAssignee} onChange={(e: any) => setTaskAssignee(e.target.value)}>
          <option value="">Asignar a...</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.full_name}
            </option>
          ))}
        </select>
        <button className="btn-ghost" onClick={addTask} disabled={!primaryOpp}>
          Crear tarea
        </button>
      </div>
    </aside>
  );
}

function OpenTasksList({ opportunityId }: { opportunityId: import("../types").Id | undefined }) {
  const [tasks, setTasks] = useState<any[]>([]);
  useEffect(() => {
    if (!opportunityId) return;
    api
      .getTasks("mine")
      .then((all) => setTasks(all.filter((t) => String(t.opportunity_id) === String(opportunityId) && t.status !== "done")))
      .catch(() => {});
  }, [opportunityId]);
  if (tasks.length === 0) return <p className="meta">Sin tareas abiertas.</p>;
  return (
    <ul>
      {tasks.map((t) => (
        <li key={t.id}>
          {t.title} — {formatDateTime(t.due_at)}
        </li>
      ))}
    </ul>
  );
}
